import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../supabase'
import AdminNav from '../components/AdminNav'
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'

const BLUE   = '#1877F2'
const TEXT   = '#050505'
const MUTED  = '#65676B'
const BORDER = '#E4E6EB'
const BG     = '#F0F2F5'
const WHITE  = '#FFFFFF'
const GREEN  = '#16A34A'
const AMBER  = '#D97706'
const RED    = '#DC2626'
const PURPLE = '#7C3AED'
const TEAL   = '#0D9488'

const PLAN_COLORS = {
  trial: AMBER, starter: MUTED, growth: BLUE,
  pro: PURPLE, business: GREEN, suspended: RED,
}

function Icon({ path, size = 15, color = MUTED, sw = 1.8 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
      <path d={path} />
    </svg>
  )
}

const IC = {
  sellers:  'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z',
  orders:   'M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z',
  revenue:  'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  trial:    'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
  verify:   'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  help:     'M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  gift:     'M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7',
  warning:  'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
  activity: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
  boost:    'M13 10V3L4 14h7v7l9-11h-7z',
  flag:     'M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9',
  users:    'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
}

function KpiCard({ label, value, sub, subUp, iconPath, accent, onClick }) {
  return (
    <div onClick={onClick}
      style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, borderLeft: '3px solid ' + accent, padding: '16px 18px', cursor: onClick ? 'pointer' : 'default', transition: 'box-shadow 0.15s', fontFamily: 'Space Grotesk, sans-serif' }}
      onMouseEnter={e => { if (onClick) e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)' }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '8px' }}>
        <span style={{ fontSize: '11.5px', fontWeight: 500, color: MUTED }}>{label}</span>
        <div style={{ width: '28px', height: '28px', borderRadius: '7px', background: accent + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon path={iconPath} color={accent} size={13} sw={2} />
        </div>
      </div>
      <div style={{ fontSize: '24px', fontWeight: 800, color: TEXT, letterSpacing: '-0.5px', marginBottom: '4px' }}>{value}</div>
      {sub && <div style={{ fontSize: '11px', color: subUp === true ? GREEN : subUp === false ? RED : MUTED, fontWeight: 500 }}>{sub}</div>}
    </div>
  )
}

function Card({ title, sub, action, children, noPad }) {
  return (
    <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, overflow: 'hidden', fontFamily: 'Space Grotesk, sans-serif' }}>
      {title && (
        <div style={{ padding: '12px 18px', borderBottom: '1px solid ' + BORDER, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT }}>{title}</div>
            {sub && <div style={{ fontSize: '11px', color: MUTED, marginTop: '2px' }}>{sub}</div>}
          </div>
          {action}
        </div>
      )}
      <div style={noPad ? {} : { padding: '16px 18px' }}>{children}</div>
    </div>
  )
}

function Pill({ label, color, bg }) {
  return (
    <span style={{ fontSize: '10.5px', fontWeight: 700, padding: '2px 8px', borderRadius: '20px', background: bg, color, textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
      {label}
    </span>
  )
}

const PAGE_SIZE = 8

function AdminDashboard() {
  const navigate = useNavigate()

  const [stats, setStats]               = useState({ totalSellers: 0, trialSellers: 0, paidSellers: 0, suspendedSellers: 0, totalOrders: 0, totalRevenue: 0, newToday: 0, pendingHelp: 0, pendingVerifications: 0, pendingReferrals: 0, totalReferralsBDT: 0, totalReferralsUSD: 0, activeBoosts: 0, pendingBoosts: 0, boostRevenue: 0, communityFlags: 0, highRiskFlags: 0 })
  const [sellers, setSellers]           = useState([])
  const [revenueChart, setRevenueChart] = useState([])
  const [signupChart, setSignupChart]   = useState([])
  const [planChart, setPlanChart]       = useState([])
  const [referrals, setReferrals]       = useState([])
  const [activity, setActivity]         = useState([])
  const [topSellers, setTopSellers]     = useState([])
  const [boosts, setBoosts]             = useState([])
  const [boostChart, setBoostChart]     = useState([])
  const [boostStatusChart, setBoostStatusChart] = useState([])
  const [topBoostSellers, setTopBoostSellers]   = useState([])
  const [communityFlags, setCommunityFlags]     = useState([])
  const [loading, setLoading]           = useState(true)
  const [search, setSearch]             = useState('')
  const [page, setPage]                 = useState(1)
  const [activeTab, setActiveTab]       = useState('overview')

  useEffect(() => { fetchAll() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    setLoading(true)

    const [sellersRes, ordersRes, helpRes, verifyRes, refRes, actRes, boostsRes, flagsRes, feeRes] = await Promise.all([
      supabase.from('sellers').select('id,plan,created_at,shop_name,email,phone,verification_status,seller_currency,country').order('created_at', { ascending: false }),
      supabase.from('orders').select('total_amount,created_at,seller_id').order('created_at', { ascending: true }),
      supabase.from('help_requests').select('id').eq('status', 'new'),
      supabase.from('sellers').select('id').eq('verification_status', 'pending'),
      supabase.from('referrals').select('*,referrer:referrer_seller_id(shop_name),referee:referee_seller_id(shop_name)').order('created_at', { ascending: false }).limit(8),
      supabase.from('activity_logs').select('*').order('created_at', { ascending: false }).limit(10),
      supabase.from('boosts').select('*,sellers(shop_name)').order('created_at', { ascending: false }),
      supabase.from('community_flags').select('*').order('last_flagged_at', { ascending: false }),
      supabase.from('boost_package_purchases').select('pageconnect_fee_bdt,created_at').order('created_at', { ascending: false }),
    ])

    const allSellers  = sellersRes.data  || []
    const allOrders   = ordersRes.data   || []
    const allRefs     = refRes.data      || []
    const allBoosts   = boostsRes.data   || []
    const allFlags    = flagsRes.data    || []
    const allFees     = feeRes.data      || []

    const today = new Date(); today.setHours(0, 0, 0, 0)
    const totalRevenue  = allOrders.reduce((s, o) => s + Number(o.total_amount || 0), 0)
    const boostRevenue  = allFees.reduce((s, f) => s + Number(f.pageconnect_fee_bdt || 0), 0)

    setStats({
      totalSellers:         allSellers.length,
      trialSellers:         allSellers.filter(s => s.plan === 'trial').length,
      paidSellers:          allSellers.filter(s => s.plan !== 'trial' && s.plan !== 'suspended').length,
      suspendedSellers:     allSellers.filter(s => s.plan === 'suspended').length,
      totalOrders:          allOrders.length,
      totalRevenue,
      newToday:             allSellers.filter(s => new Date(s.created_at) >= today).length,
      pendingHelp:          (helpRes.data || []).length,
      pendingVerifications: (verifyRes.data || []).length,
      pendingReferrals:     allRefs.filter(r => r.status === 'pending' || r.status === 'approved').length,
      totalReferralsBDT:    allRefs.filter(r => r.currency === 'BDT' && r.status === 'paid').reduce((s, r) => s + Number(r.discount_amount || 0), 0),
      totalReferralsUSD:    allRefs.filter(r => r.currency === 'USD' && r.status === 'paid').reduce((s, r) => s + Number(r.discount_amount || 0), 0),
      activeBoosts:         allBoosts.filter(b => b.status === 'running').length,
      pendingBoosts:        allBoosts.filter(b => b.status === 'pending' || b.status === 'submitted').length,
      boostRevenue,
      communityFlags:       allFlags.length,
      highRiskFlags:        allFlags.filter(f => f.level === 'high_risk').length,
    })

    setSellers(allSellers)
    setReferrals(allRefs)
    setActivity(actRes.data || [])
    setBoosts(allBoosts)
    setCommunityFlags(allFlags)

    // Top sellers by order revenue
    const sellerOrderMap = {}
    allOrders.forEach(o => {
      if (!o.seller_id) return
      sellerOrderMap[o.seller_id] = (sellerOrderMap[o.seller_id] || 0) + Number(o.total_amount || 0)
    })
    setTopSellers(
      allSellers.map(s => ({ ...s, revenue: sellerOrderMap[s.id] || 0 }))
        .sort((a, b) => b.revenue - a.revenue).slice(0, 5)
    )

    // Top boost sellers by spend
    const boostSpendMap = {}
    allBoosts.forEach(b => {
      if (!b.seller_id) return
      boostSpendMap[b.seller_id] = (boostSpendMap[b.seller_id] || 0) + Number(b.budget_bdt || 0)
    })
    setTopBoostSellers(
      allSellers.filter(s => boostSpendMap[s.id]).map(s => ({ ...s, boostSpend: boostSpendMap[s.id] || 0 }))
        .sort((a, b) => b.boostSpend - a.boostSpend).slice(0, 5)
    )

    // Revenue chart last 6 months
    const mnMap = {}
    allOrders.forEach(o => {
      const key = new Date(o.created_at).toLocaleDateString('en-BD', { month: 'short', year: '2-digit' })
      if (!mnMap[key]) mnMap[key] = { month: key, revenue: 0, orders: 0 }
      mnMap[key].revenue += Number(o.total_amount || 0)
      mnMap[key].orders  += 1
    })
    setRevenueChart(Object.values(mnMap).slice(-6))

    // Signup trend
    const wkMap = {}
    allSellers.forEach(s => {
      const key = new Date(s.created_at).toLocaleDateString('en-BD', { month: 'short', day: 'numeric' })
      wkMap[key] = (wkMap[key] || 0) + 1
    })
    setSignupChart(Object.entries(wkMap).slice(-8).map(([w, v]) => ({ week: w, signups: v })))

    // Plan breakdown
    const planMap = {}
    allSellers.forEach(s => { planMap[s.plan || 'trial'] = (planMap[s.plan || 'trial'] || 0) + 1 })
    setPlanChart(Object.entries(planMap).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value))

    // Boost submissions per week
    const bwMap = {}
    allBoosts.forEach(b => {
      const key = new Date(b.created_at).toLocaleDateString('en-BD', { month: 'short', day: 'numeric' })
      bwMap[key] = (bwMap[key] || 0) + 1
    })
    setBoostChart(Object.entries(bwMap).slice(-8).map(([w, v]) => ({ week: w, boosts: v })))

    // Boost status breakdown
    const bsMap = {}
    allBoosts.forEach(b => { bsMap[b.status] = (bsMap[b.status] || 0) + 1 })
    setBoostStatusChart(Object.entries(bsMap).map(([name, value]) => ({ name, value })))

    setLoading(false)
  }

  const fmt = (n) => {
    const v = Math.abs(Math.round(n || 0))
    if (v >= 1000000) return '৳' + (v / 1000000).toFixed(1) + 'M'
    if (v >= 100000)  return '৳' + Math.round(v / 1000) + 'k'
    return '৳' + v.toLocaleString()
  }

  const navBtn = (label, path) => (
    <button onClick={() => navigate(path)}
      style={{ fontSize: '11.5px', fontWeight: 600, color: BLUE, background: '#EFF6FF', border: 'none', padding: '5px 11px', borderRadius: '7px', cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>
      {label}
    </button>
  )

  const filtered    = sellers.filter(s => { if (!search) return true; const q = search.toLowerCase(); return s.shop_name?.toLowerCase().includes(q) || s.email?.toLowerCase().includes(q) || s.phone?.includes(q) })
  const totalPages  = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const pageSellers = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const ACT_COLORS = { status_changed: BLUE, order_edited: PURPLE, order_deleted: RED, dash_order_created: AMBER, product_edited: GREEN, product_deleted: RED, stock_updated: AMBER }
  const ACT_LABELS = { status_changed: 'Status updated', order_edited: 'Order edited', order_deleted: 'Order deleted', dash_order_created: 'Dash order created', product_edited: 'Product updated', product_deleted: 'Product deleted', stock_updated: 'Stock updated' }

  const timeAgo = (d) => { const s = Math.floor((new Date() - new Date(d)) / 1000); if (s < 60) return 'just now'; if (s < 3600) return Math.floor(s / 60) + 'm ago'; if (s < 86400) return Math.floor(s / 3600) + 'h ago'; return Math.floor(s / 86400) + 'd ago' }

  const REF_STATUS  = { pending: { bg: '#FFFBEB', color: AMBER }, approved: { bg: '#EFF6FF', color: BLUE }, paid: { bg: '#F0FDF4', color: GREEN }, rejected: { bg: '#FEF2F2', color: RED } }
  const BOOST_STATUS = { pending: { bg: '#FFFBEB', color: AMBER }, submitted: { bg: '#EFF6FF', color: BLUE }, running: { bg: '#F0FDF4', color: GREEN }, completed: { bg: '#F5F5F5', color: MUTED }, rejected: { bg: '#FEF2F2', color: RED } }
  const FLAG_LEVEL  = { watch: { bg: '#FFFBEB', color: AMBER }, caution: { bg: '#FFF7ED', color: '#EA580C' }, high_risk: { bg: '#FEF2F2', color: RED } }

  const TABS = ['overview', 'sellers', 'boosts', 'flags', 'referrals', 'activity']

  const BOOST_PIE_COLORS = [AMBER, BLUE, GREEN, MUTED, RED]

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: BG, fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
      <AdminNav />
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        <div style={{ padding: '24px 28px' }}>
          <style>{`
            @media(max-width:1100px){ .adm-charts{grid-template-columns:1fr!important} }
            @media(max-width:960px){ .adm-kpi{grid-template-columns:1fr 1fr!important} .adm-bottom{grid-template-columns:1fr!important} }
            @media(max-width:600px){ .adm-kpi{grid-template-columns:1fr!important} }
          `}</style>

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 800, color: TEXT, letterSpacing: '-0.3px' }}>Platform Dashboard</div>
              <div style={{ fontSize: '12.5px', color: MUTED, marginTop: '2px' }}>All sellers — real-time overview</div>
            </div>
            <button onClick={fetchAll}
              style={{ fontSize: '12px', fontWeight: 600, color: MUTED, background: WHITE, border: '1px solid ' + BORDER, padding: '7px 14px', borderRadius: '9px', cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>
              ↻ Refresh
            </button>
          </div>

          {/* Alert banners */}
          {(stats.pendingHelp > 0 || stats.pendingVerifications > 0 || stats.pendingReferrals > 0 || stats.pendingBoosts > 0 || stats.highRiskFlags > 0) && (
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
              {stats.pendingHelp > 0 && (
                <div onClick={() => navigate('/admin/help')}
                  style={{ display: 'flex', alignItems: 'center', gap: '7px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '9px', padding: '8px 14px', cursor: 'pointer' }}>
                  <Icon path={IC.warning} color={AMBER} size={13} sw={2} />
                  <span style={{ fontSize: '12px', fontWeight: 600, color: AMBER }}>{stats.pendingHelp} help pending</span>
                </div>
              )}
              {stats.pendingVerifications > 0 && (
                <div onClick={() => navigate('/admin/verifications')}
                  style={{ display: 'flex', alignItems: 'center', gap: '7px', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '9px', padding: '8px 14px', cursor: 'pointer' }}>
                  <Icon path={IC.verify} color={BLUE} size={13} sw={2} />
                  <span style={{ fontSize: '12px', fontWeight: 600, color: BLUE }}>{stats.pendingVerifications} verifications pending</span>
                </div>
              )}
              {stats.pendingBoosts > 0 && (
                <div onClick={() => setActiveTab('boosts')}
                  style={{ display: 'flex', alignItems: 'center', gap: '7px', background: '#F0FDFA', border: '1px solid #99F6E4', borderRadius: '9px', padding: '8px 14px', cursor: 'pointer' }}>
                  <Icon path={IC.boost} color={TEAL} size={13} sw={2} />
                  <span style={{ fontSize: '12px', fontWeight: 600, color: TEAL }}>{stats.pendingBoosts} boost approvals needed</span>
                </div>
              )}
              {stats.highRiskFlags > 0 && (
                <div onClick={() => setActiveTab('flags')}
                  style={{ display: 'flex', alignItems: 'center', gap: '7px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '9px', padding: '8px 14px', cursor: 'pointer' }}>
                  <Icon path={IC.flag} color={RED} size={13} sw={2} />
                  <span style={{ fontSize: '12px', fontWeight: 600, color: RED }}>{stats.highRiskFlags} high-risk flags</span>
                </div>
              )}
              {stats.pendingReferrals > 0 && (
                <div onClick={() => navigate('/admin/referrals')}
                  style={{ display: 'flex', alignItems: 'center', gap: '7px', background: '#F5F3FF', border: '1px solid #DDD6FE', borderRadius: '9px', padding: '8px 14px', cursor: 'pointer' }}>
                  <Icon path={IC.gift} color={PURPLE} size={13} sw={2} />
                  <span style={{ fontSize: '12px', fontWeight: 600, color: PURPLE }}>{stats.pendingReferrals} referral payouts due</span>
                </div>
              )}
            </div>
          )}

          {loading && (
            <div style={{ textAlign: 'center', padding: '60px', color: MUTED, fontSize: '13px' }}>Loading platform data...</div>
          )}

          {!loading && (
            <div>
              {/* KPI Row 1 — Sellers & Orders */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '10px', marginBottom: '10px' }} className="adm-kpi">
                <KpiCard label="Total sellers"     value={stats.totalSellers}                  sub={'+' + stats.newToday + ' joined today'} subUp={true}  iconPath={IC.sellers} accent={BLUE}   onClick={() => setActiveTab('sellers')} />
                <KpiCard label="On trial"          value={stats.trialSellers}                  sub={stats.paidSellers + ' paid · ' + stats.suspendedSellers + ' suspended'}       iconPath={IC.trial}   accent={AMBER}  onClick={() => setActiveTab('sellers')} />
                <KpiCard label="Total orders"      value={stats.totalOrders.toLocaleString()}  sub="All sellers all time"                                iconPath={IC.orders}  accent={PURPLE} />
                <KpiCard label="Revenue processed" value={fmt(stats.totalRevenue)}             sub="All sellers GMV" subUp={true}                       iconPath={IC.revenue} accent={GREEN} />
              </div>

              {/* KPI Row 2 — Boosts & Flags */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '10px', marginBottom: '10px' }} className="adm-kpi">
                <KpiCard label="Active boosts"     value={stats.activeBoosts}                  sub={stats.pendingBoosts + ' pending approval'} subUp={stats.pendingBoosts === 0} iconPath={IC.boost} accent={TEAL}   onClick={() => setActiveTab('boosts')} />
                <KpiCard label="Boost revenue"     value={fmt(stats.boostRevenue)}             sub="PageConnect service fees" subUp={true}                                       iconPath={IC.revenue} accent={TEAL} />
                <KpiCard label="Community flags"   value={stats.communityFlags}                sub={stats.highRiskFlags + ' high risk'} subUp={stats.highRiskFlags === 0}         iconPath={IC.flag}  accent={RED}    onClick={() => setActiveTab('flags')} />
                <KpiCard label="Help & support"    value={stats.pendingHelp}                   sub={stats.pendingHelp > 0 ? 'Need response' : 'All clear'} subUp={stats.pendingHelp === 0} iconPath={IC.help} accent={AMBER} onClick={() => navigate('/admin/help')} />
              </div>

              {/* KPI Row 3 — Admin & Referrals */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '10px', marginBottom: '16px' }} className="adm-kpi">
                <KpiCard label="Pending verifications" value={stats.pendingVerifications} sub={stats.pendingVerifications > 0 ? 'Need review' : 'All clear'} subUp={stats.pendingVerifications === 0} iconPath={IC.verify} accent={BLUE}   onClick={() => navigate('/admin/verifications')} />
                <KpiCard label="Referral payouts due"  value={stats.pendingReferrals}     sub="Pending + approved"                                           iconPath={IC.gift}   accent={PURPLE} onClick={() => navigate('/admin/referrals')} />
                <KpiCard label="Referrals paid BDT"    value={'৳' + stats.totalReferralsBDT} sub="Total disbursed" subUp={true}                              iconPath={IC.gift}   accent={GREEN} />
                <KpiCard label="Admin users"           value="—"                          sub="Manage team access"                                           iconPath={IC.users}  accent={MUTED}  onClick={() => navigate('/admin/users')} />
              </div>

              {/* Tabs */}
              <div style={{ display: 'flex', gap: '4px', padding: '4px', background: WHITE, border: '1px solid ' + BORDER, borderRadius: '10px', marginBottom: '16px', width: 'fit-content', flexWrap: 'wrap' }}>
                {TABS.map(tab => (
                  <button key={tab} onClick={() => setActiveTab(tab)}
                    style={{ padding: '7px 16px', borderRadius: '7px', border: 'none', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', background: activeTab === tab ? BLUE : 'transparent', color: activeTab === tab ? WHITE : MUTED, fontFamily: 'Space Grotesk, sans-serif', textTransform: 'capitalize', transition: 'all 0.12s' }}>
                    {tab}
                  </button>
                ))}
              </div>

              {/* ── OVERVIEW TAB ── */}
              {activeTab === 'overview' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '14px' }} className="adm-charts">
                    <Card title="Revenue processed" sub="Last 6 months — all sellers GMV">
                      <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={revenueChart} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
                          <XAxis dataKey="month" tick={{ fontSize: 11, fill: MUTED }} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fontSize: 11, fill: MUTED }} width={50} axisLine={false} tickLine={false} tickFormatter={v => v >= 1000 ? '৳' + Math.round(v / 1000) + 'k' : '৳' + v} />
                          <Tooltip contentStyle={{ border: '1px solid ' + BORDER, borderRadius: '10px', fontSize: '12px', fontFamily: 'Space Grotesk' }} formatter={v => ['৳' + v.toLocaleString(), 'Revenue']} />
                          <Bar dataKey="revenue" fill={BLUE} radius={[5, 5, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Card>
                    <Card title="Plan breakdown" sub="All sellers by plan">
                      <ResponsiveContainer width="100%" height={120}>
                        <PieChart>
                          <Pie data={planChart} cx="50%" cy="50%" innerRadius={30} outerRadius={52} paddingAngle={3} dataKey="value" strokeWidth={0}>
                            {planChart.map((entry, i) => <Cell key={i} fill={PLAN_COLORS[entry.name] || '#999'} />)}
                          </Pie>
                          <Tooltip contentStyle={{ border: '1px solid ' + BORDER, borderRadius: '8px', fontSize: '11px' }} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '6px' }}>
                        {planChart.map((p, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: PLAN_COLORS[p.name] || '#999' }} />
                              <span style={{ fontSize: '11.5px', color: TEXT, textTransform: 'capitalize' }}>{p.name}</span>
                            </div>
                            <span style={{ fontSize: '12px', fontWeight: 700, color: MUTED }}>{p.value}</span>
                          </div>
                        ))}
                      </div>
                    </Card>
                  </div>

                  <Card title="Seller signups" sub="Weekly trend">
                    <ResponsiveContainer width="100%" height={100}>
                      <LineChart data={signupChart} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
                        <XAxis dataKey="week" tick={{ fontSize: 11, fill: MUTED }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: MUTED }} width={24} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={{ border: '1px solid ' + BORDER, borderRadius: '10px', fontSize: '12px', fontFamily: 'Space Grotesk' }} />
                        <Line type="monotone" dataKey="signups" stroke={GREEN} strokeWidth={2.5} dot={{ fill: GREEN, r: 3, strokeWidth: 0 }} activeDot={{ r: 5 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </Card>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }} className="adm-bottom">
                    <Card title="Top sellers" sub="By total revenue processed" action={navBtn('All sellers', '/admin/sellers')} noPad>
                      {topSellers.map((s, i) => (
                        <div key={s.id} onClick={() => { setSearch(s.shop_name); setActiveTab('sellers') }}
                          style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 18px', borderBottom: i < topSellers.length - 1 ? '1px solid #F5F5F5' : 'none', cursor: 'pointer' }}
                          onMouseEnter={e => e.currentTarget.style.background = BG}
                          onMouseLeave={e => e.currentTarget.style.background = WHITE}>
                          <div style={{ width: '20px', textAlign: 'center', fontSize: '13px', flexShrink: 0 }}>
                            {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : <span style={{ fontSize: '11px', color: MUTED }}>{i + 1}</span>}
                          </div>
                          <div style={{ width: '28px', height: '28px', borderRadius: '7px', background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, color: BLUE, flexShrink: 0 }}>
                            {s.shop_name?.charAt(0).toUpperCase()}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '12.5px', fontWeight: 600, color: TEXT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.shop_name}</div>
                            <div style={{ fontSize: '11px', color: MUTED }}>{s.country || s.seller_currency}</div>
                          </div>
                          <div style={{ fontSize: '13px', fontWeight: 700, color: BLUE, flexShrink: 0 }}>{fmt(s.revenue)}</div>
                        </div>
                      ))}
                    </Card>

                    <Card title="Recent referrals" sub="Latest activity" action={navBtn('Manage', '/admin/referrals')} noPad>
                      {referrals.slice(0, 5).map((ref, i) => {
                        const sc = REF_STATUS[ref.status] || REF_STATUS.pending
                        return (
                          <div key={ref.id} style={{ padding: '10px 18px', borderBottom: i < 4 ? '1px solid #F5F5F5' : 'none' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '3px' }}>
                              <span style={{ fontSize: '12px', fontWeight: 600, color: TEXT }}>
                                {ref.referrer?.shop_name || '—'}<span style={{ fontWeight: 400, color: MUTED, margin: '0 5px' }}>to</span>{ref.referee?.shop_name || '—'}
                              </span>
                              <Pill label={ref.status} color={sc.color} bg={sc.bg} />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <span style={{ fontSize: '10.5px', fontFamily: 'monospace', color: MUTED }}>{ref.promo_code}</span>
                              {ref.discount_amount && <span style={{ fontSize: '12px', fontWeight: 700, color: BLUE }}>{ref.currency === 'BDT' ? '৳' : '$'}{ref.discount_amount}</span>}
                            </div>
                          </div>
                        )
                      })}
                      {referrals.length === 0 && <div style={{ padding: '32px', textAlign: 'center', color: MUTED, fontSize: '12px' }}>No referrals yet</div>}
                    </Card>
                  </div>
                </div>
              )}

              {/* ── SELLERS TAB ── */}
              {activeTab === 'sellers' && (
                <Card title="All sellers" sub={filtered.length + ' sellers'} action={navBtn('Full page', '/admin/sellers')} noPad>
                  <div style={{ padding: '12px 18px', borderBottom: '1px solid ' + BORDER }}>
                    <input type="text" placeholder="Search by name, email or phone..." value={search} onChange={e => { setSearch(e.target.value); setPage(1) }}
                      style={{ width: '100%', border: '1px solid ' + BORDER, borderRadius: '9px', padding: '8px 12px', fontSize: '12.5px', outline: 'none', fontFamily: 'Space Grotesk, sans-serif', color: TEXT, background: BG, boxSizing: 'border-box' }}
                      onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER} />
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                      <thead>
                        <tr style={{ background: BG }}>
                          {['#', 'Shop', 'Email', 'Country', 'Plan', 'Verification', 'Joined'].map(h => (
                            <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontSize: '10px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid ' + BORDER }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {pageSellers.map((s, i) => (
                          <tr key={s.id} onClick={() => navigate('/admin/sellers')} style={{ borderBottom: '1px solid #F5F5F5', cursor: 'pointer' }}
                            onMouseEnter={e => e.currentTarget.style.background = BG} onMouseLeave={e => e.currentTarget.style.background = WHITE}>
                            <td style={{ padding: '9px 14px', fontSize: '11px', color: MUTED, fontWeight: 600 }}>{(page - 1) * PAGE_SIZE + i + 1}</td>
                            <td style={{ padding: '9px 14px', fontSize: '12.5px', fontWeight: 700, color: TEXT }}>{s.shop_name}</td>
                            <td style={{ padding: '9px 14px', fontSize: '11.5px', color: MUTED }}>{s.email}</td>
                            <td style={{ padding: '9px 14px', fontSize: '11.5px', color: MUTED }}>{s.country || s.seller_currency || '—'}</td>
                            <td style={{ padding: '9px 14px' }}><Pill label={s.plan || 'trial'} color={PLAN_COLORS[s.plan] || MUTED} bg={(PLAN_COLORS[s.plan] || MUTED) + '18'} /></td>
                            <td style={{ padding: '9px 14px' }}><Pill label={s.verification_status || 'unverified'} color={s.verification_status === 'verified' ? GREEN : s.verification_status === 'pending' ? BLUE : MUTED} bg={s.verification_status === 'verified' ? '#F0FDF4' : s.verification_status === 'pending' ? '#EFF6FF' : BG} /></td>
                            <td style={{ padding: '9px 14px', fontSize: '11.5px', color: MUTED }}>{new Date(s.created_at).toLocaleDateString('en-BD', { day: 'numeric', month: 'short', year: '2-digit' })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ padding: '10px 18px', borderTop: '1px solid ' + BORDER, background: BG, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '12px', color: MUTED }}>Showing <strong style={{ color: TEXT }}>{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)}</strong> of <strong style={{ color: TEXT }}>{filtered.length}</strong></span>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ width: '28px', height: '28px', borderRadius: '7px', border: '1px solid ' + BORDER, background: WHITE, cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.4 : 1, fontSize: '14px' }}>‹</button>
                      <span style={{ padding: '0 10px', lineHeight: '28px', fontSize: '12px', fontWeight: 600, color: TEXT }}>{page}/{totalPages}</span>
                      <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ width: '28px', height: '28px', borderRadius: '7px', border: '1px solid ' + BORDER, background: WHITE, cursor: page === totalPages ? 'not-allowed' : 'pointer', opacity: page === totalPages ? 0.4 : 1, fontSize: '14px' }}>›</button>
                    </div>
                  </div>
                </Card>
              )}

              {/* ── BOOSTS TAB ── */}
              {activeTab === 'boosts' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '10px' }} className="adm-kpi">
                    <KpiCard label="Total boosts"    value={boosts.length}                                         iconPath={IC.boost}   accent={TEAL} />
                    <KpiCard label="Active now"      value={stats.activeBoosts}       sub="Currently running"      iconPath={IC.boost}   accent={GREEN} />
                    <KpiCard label="Pending review"  value={stats.pendingBoosts}      sub={stats.pendingBoosts > 0 ? 'Need approval' : 'All clear'} subUp={stats.pendingBoosts === 0} iconPath={IC.warning} accent={AMBER} />
                    <KpiCard label="Boost fees earned" value={fmt(stats.boostRevenue)} sub="Service fees collected" subUp={true}          iconPath={IC.revenue} accent={TEAL} />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '14px' }} className="adm-charts">
                    <Card title="Boost submissions" sub="Weekly trend">
                      <ResponsiveContainer width="100%" height={160}>
                        <BarChart data={boostChart} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
                          <XAxis dataKey="week" tick={{ fontSize: 11, fill: MUTED }} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fontSize: 11, fill: MUTED }} width={24} axisLine={false} tickLine={false} />
                          <Tooltip contentStyle={{ border: '1px solid ' + BORDER, borderRadius: '10px', fontSize: '12px', fontFamily: 'Space Grotesk' }} />
                          <Bar dataKey="boosts" fill={TEAL} radius={[5, 5, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Card>
                    <Card title="Status breakdown" sub="All boosts">
                      <ResponsiveContainer width="100%" height={120}>
                        <PieChart>
                          <Pie data={boostStatusChart} cx="50%" cy="50%" innerRadius={28} outerRadius={50} paddingAngle={3} dataKey="value" strokeWidth={0}>
                            {boostStatusChart.map((_, i) => <Cell key={i} fill={BOOST_PIE_COLORS[i % BOOST_PIE_COLORS.length]} />)}
                          </Pie>
                          <Tooltip contentStyle={{ border: '1px solid ' + BORDER, borderRadius: '8px', fontSize: '11px' }} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '6px' }}>
                        {boostStatusChart.map((s, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: BOOST_PIE_COLORS[i % BOOST_PIE_COLORS.length] }} />
                              <span style={{ fontSize: '11.5px', color: TEXT, textTransform: 'capitalize' }}>{s.name}</span>
                            </div>
                            <span style={{ fontSize: '12px', fontWeight: 700, color: MUTED }}>{s.value}</span>
                          </div>
                        ))}
                      </div>
                    </Card>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }} className="adm-bottom">
                    <Card title="Top boost sellers" sub="By total ad spend" noPad>
                      {topBoostSellers.length === 0 ? (
                        <div style={{ padding: '32px', textAlign: 'center', color: MUTED, fontSize: '12px' }}>No boost data yet</div>
                      ) : topBoostSellers.map((s, i) => (
                        <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 18px', borderBottom: i < topBoostSellers.length - 1 ? '1px solid #F5F5F5' : 'none' }}>
                          <div style={{ width: '20px', textAlign: 'center', fontSize: '13px', flexShrink: 0 }}>
                            {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : <span style={{ fontSize: '11px', color: MUTED }}>{i + 1}</span>}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '12.5px', fontWeight: 600, color: TEXT }}>{s.shop_name}</div>
                            <div style={{ fontSize: '11px', color: MUTED }}>{boosts.filter(b => b.seller_id === s.id).length} campaigns</div>
                          </div>
                          <div style={{ fontSize: '13px', fontWeight: 700, color: TEAL }}>{fmt(s.boostSpend)}</div>
                        </div>
                      ))}
                    </Card>

                    <Card title="Recent boosts" sub="Latest submissions" action={navBtn('Manage', '/admin/boosts')} noPad>
                      {boosts.slice(0, 6).map((b, i) => {
                        const sc = BOOST_STATUS[b.status] || BOOST_STATUS.pending
                        return (
                          <div key={b.id} style={{ padding: '10px 18px', borderBottom: i < 5 ? '1px solid #F5F5F5' : 'none' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '3px' }}>
                              <span style={{ fontSize: '12.5px', fontWeight: 600, color: TEXT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '60%' }}>{b.fb_campaign_name || 'Unnamed boost'}</span>
                              <Pill label={b.status} color={sc.color} bg={sc.bg} />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: MUTED }}>
                              <span>{b.sellers?.shop_name || '—'}</span>
                              <span style={{ fontWeight: 600, color: TEAL }}>{fmt(b.budget_bdt)}</span>
                            </div>
                          </div>
                        )
                      })}
                      {boosts.length === 0 && <div style={{ padding: '32px', textAlign: 'center', color: MUTED, fontSize: '12px' }}>No boosts yet</div>}
                    </Card>
                  </div>
                </div>
              )}

              {/* ── FLAGS TAB ── */}
              {activeTab === 'flags' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '10px' }} className="adm-kpi">
                    <KpiCard label="Community flags" value={communityFlags.length}                                        iconPath={IC.flag} accent={RED} />
                    <KpiCard label="High risk"       value={communityFlags.filter(f => f.level === 'high_risk').length}   iconPath={IC.flag} accent={RED} />
                    <KpiCard label="Watch / Caution" value={communityFlags.filter(f => f.level !== 'high_risk').length}   iconPath={IC.flag} accent={AMBER} onClick={() => navigate('/admin/flags')} />
                  </div>

                  <Card title="Community flagged customers" sub="3+ sellers reported same phone" action={navBtn('Full management', '/admin/flags')} noPad>
                    {communityFlags.length === 0 ? (
                      <div style={{ padding: '40px', textAlign: 'center', color: MUTED, fontSize: '12px' }}>
                        <div style={{ fontSize: '32px', marginBottom: '8px' }}>✓</div>
                        No community flags — the platform looks clean!
                      </div>
                    ) : communityFlags.slice(0, 10).map((f, i) => {
                      const lc = FLAG_LEVEL[f.level] || FLAG_LEVEL.watch
                      return (
                        <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '11px 18px', borderBottom: i < Math.min(communityFlags.length, 10) - 1 ? '1px solid #F5F5F5' : 'none' }}>
                          <div style={{ fontSize: '11px', color: MUTED, width: '20px', textAlign: 'center', flexShrink: 0 }}>{i + 1}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '12.5px', fontWeight: 700, color: TEXT, fontFamily: 'JetBrains Mono, monospace' }}>{f.phone}</div>
                            <div style={{ fontSize: '11px', color: MUTED, marginTop: '2px' }}>
                              {f.flag_count} seller{f.flag_count !== 1 ? 's' : ''} · {f.dominant_reason?.replace(/_/g, ' ') || 'various reasons'}
                            </div>
                          </div>
                          <Pill label={f.level?.replace('_', ' ') || 'watch'} color={lc.color} bg={lc.bg} />
                        </div>
                      )
                    })}
                  </Card>
                </div>
              )}

              {/* ── REFERRALS TAB ── */}
              {activeTab === 'referrals' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '10px' }} className="adm-kpi">
                    <KpiCard label="Total referrals" value={referrals.length}                                                          iconPath={IC.gift} accent={BLUE} />
                    <KpiCard label="Pending"         value={referrals.filter(r => r.status === 'pending').length}                      iconPath={IC.gift} accent={AMBER} onClick={() => navigate('/admin/referrals')} />
                    <KpiCard label="Paid out BDT"    value={'৳' + stats.totalReferralsBDT} sub="Total disbursed" subUp={true}          iconPath={IC.gift} accent={GREEN} />
                    <KpiCard label="Paid out USD"    value={'$' + stats.totalReferralsUSD} sub="Total disbursed" subUp={true}          iconPath={IC.gift} accent={PURPLE} />
                  </div>
                  <Card title="Recent referrals" sub="Latest 8 referral records" action={navBtn('Full tracker', '/admin/referrals')} noPad>
                    {referrals.map((ref, i) => {
                      const sc = REF_STATUS[ref.status] || REF_STATUS.pending
                      return (
                        <div key={ref.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '11px 18px', borderBottom: i < referrals.length - 1 ? '1px solid #F5F5F5' : 'none' }}>
                          <div style={{ fontSize: '11px', color: MUTED, width: '20px', textAlign: 'center', flexShrink: 0 }}>{i + 1}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '12.5px', fontWeight: 600, color: TEXT }}>{ref.referrer?.shop_name || '—'}<span style={{ fontWeight: 400, color: MUTED, margin: '0 6px' }}>→</span>{ref.referee?.shop_name || '—'}</div>
                            <div style={{ fontSize: '11px', color: MUTED, marginTop: '2px' }}><span style={{ fontFamily: 'monospace' }}>{ref.promo_code}</span><span style={{ margin: '0 6px' }}>·</span>{new Date(ref.created_at).toLocaleDateString('en-BD', { day: 'numeric', month: 'short', year: '2-digit' })}</div>
                          </div>
                          {ref.discount_amount && <span style={{ fontSize: '13px', fontWeight: 700, color: BLUE, flexShrink: 0 }}>{ref.currency === 'BDT' ? '৳' : '$'}{ref.discount_amount}</span>}
                          <Pill label={ref.status} color={sc.color} bg={sc.bg} />
                        </div>
                      )
                    })}
                    {referrals.length === 0 && <div style={{ padding: '40px', textAlign: 'center', color: MUTED, fontSize: '12px' }}>No referrals yet</div>}
                  </Card>
                </div>
              )}

              {/* ── ACTIVITY TAB ── */}
              {activeTab === 'activity' && (
                <Card title="Recent activity" sub="Last 10 actions across all sellers" noPad>
                  {activity.length === 0 && <div style={{ padding: '40px', textAlign: 'center', color: MUTED, fontSize: '12px' }}>No activity logged yet</div>}
                  {activity.map((log, i) => {
                    const color = ACT_COLORS[log.action] || MUTED
                    const label = ACT_LABELS[log.action] || log.action
                    return (
                      <div key={log.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '11px 18px', borderBottom: i < activity.length - 1 ? '1px solid #F5F5F5' : 'none' }}>
                        <div style={{ fontSize: '11px', color: MUTED, width: '20px', textAlign: 'center', flexShrink: 0, paddingTop: '2px' }}>{i + 1}</div>
                        <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Icon path={IC.activity} color={color} size={12} sw={2} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '12.5px', fontWeight: 600, color: TEXT }}>{label}</div>
                          {log.new_value && log.new_value.length < 30 && <div style={{ fontSize: '11px', color: MUTED, marginTop: '2px' }}>{log.entity_type} → {log.new_value}</div>}
                          <div style={{ fontSize: '11px', color: '#B0B3B8', marginTop: '2px' }}>{log.done_by || 'Owner'} · {timeAgo(log.created_at)}</div>
                        </div>
                      </div>
                    )
                  })}
                </Card>
              )}

            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AdminDashboard